import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { ArrowLeft } from 'lucide-react';

const BlogPost = () => {
  const { blogId } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await axios.get(`${API}/blog/${blogId}`);
        setBlog(response.data);
      } catch (error) {
        console.error('Error fetching blog:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [blogId]);

  if (loading) {
    return (
      <div className="pt-16 min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="pt-16 min-h-screen bg-[#050505]">
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <h1 className="font-outfit text-3xl font-bold text-white mb-4">Blog not found</h1>
          <Link to="/blog" className="text-red-500 hover:text-red-400">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen bg-[#050505]" data-testid="blog-post-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/blog" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>

        {blog.image_url && (
          <div className="mb-8 rounded-sm overflow-hidden">
            <img
              src={blog.image_url}
              alt={blog.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          </div>
        )}

        <article>
          <header className="mb-8">
            <h1 className="font-outfit text-3xl md:text-4xl font-black text-white mb-4">
              {blog.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-zinc-500">
              <span>By {blog.author}</span>
              <span>•</span>
              <span>
                {new Date(blog.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            </div>
          </header>

          <div className="prose prose-invert max-w-none">
            <div className="text-zinc-300 leading-relaxed whitespace-pre-wrap">
              {blog.content}
            </div>
          </div>
        </article>

        {/* Share Section */}
        <div className="mt-12 pt-8 border-t border-zinc-800">
          <h3 className="text-lg font-semibold text-white mb-4">Share this post</h3>
          <div className="flex gap-3">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(blog.title + ' - ' + window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-sm transition-colors"
            >
              WhatsApp
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-sm transition-colors"
            >
              Twitter
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert('Link copied!');
              }}
              className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-sm transition-colors"
            >
              Copy Link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
